import { PkButton, PkDialog } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import type { ComponentType, JSX } from 'react';
import { useRef, useMemo, memo, useEffect, useState } from 'react';

import { useWindowSize } from '../hooks/useWindowSize';
import ReactPortal from '../ReactPortal';

import { withErrorBoundary } from '@shared/ErrorBoundary';
import { useTranslateFn } from '@shared/Translate';
import type { PkTheme } from '@shared-types/common.types';
import { isEqual } from '@utilities/collection-utils';

import * as classes from './styles.module.scss';

export type FilterModalProps<T> = {
  onUpdate: (newState: T) => void;
  initialState: T;
  resetState: T;
  dialogClassName?: string;
  buttonClassName?: string;
  buttonWidth?: string;
  headerText?: string;
  errorText?: string;
  closeOnReset?: boolean;
  dialogTheme?: PkTheme;
  headerTheme?: PkTheme;
  applyBtnTag?: string;
  resetBtnTag?: string;
  onModalOpen?: () => void;
  onModalClose?: () => void;
  /** Raised when the user intentionally click the close button  */
  onModalCloseClick?: () => void;
  onResetFilters?: () => void;
  stateIsValid?: (state: T) => boolean;
  ButtonContent: ComponentType<{ state: T }>;
  Content: ComponentType<{
    initialState: T;
    localState: T;
    onContentUpdate: (newState: T | ((state: T) => T)) => void;
  }>;
  subContentNote?: JSX.Element;
};

export const FilterModal = <T,>({
  onUpdate,
  initialState,
  resetState,
  dialogClassName,
  buttonClassName,
  buttonWidth,
  headerText,
  errorText,
  closeOnReset,
  dialogTheme = 'light',
  headerTheme = 'light',
  applyBtnTag = 'apply_filters',
  resetBtnTag = 'reset_filters',
  onModalOpen,
  onModalClose,
  onModalCloseClick,
  onResetFilters,
  stateIsValid,
  ButtonContent,
  Content,
  subContentNote = null,
}: FilterModalProps<T>) => {
  const t = useTranslateFn();
  const { isMobile, isDesktop, isLandscape } = useWindowSize();
  const updateClicked = useRef(false);
  const [open, setOpen] = useState(false);
  const [localState, setLocalState] = useState<T>(initialState);
  const [validState, setValidState] = useState(true);
  const [hasError, setHasError] = useState(false);

  const changed = useMemo(() => !isEqual(localState, resetState), [localState, resetState]);

  useEffect(() => {
    stateIsValid && setValidState(stateIsValid(localState));
  }, [localState, stateIsValid]);

  useEffect(() => {
    if (open) {
      setLocalState(initialState);
      setHasError(false);
    }
  }, [open, initialState]);

  const handleUpdate = (newState: T) => {
    setLocalState(newState);
  };

  const handleFilterClick = () => {
    setOpen((state) => !state);
  };

  const handleClearFilterClick = () => {
    onResetFilters && onResetFilters();
    closeOnReset ? onUpdate(resetState) : setLocalState(resetState);
    closeOnReset && setOpen(false);
  };

  const handleConfirmClick = () => {
    if (stateIsValid && !stateIsValid(localState)) {
      setHasError(true);
      return;
    }
    updateClicked.current = true;
    onUpdate(localState);
    setOpen(false);
  };

  const handleOpen = () => {
    updateClicked.current = false;
    onModalOpen && onModalOpen();
  };

  const handleClose = () => {
    open && onModalClose && onModalClose();
    // workaround to match the click of the user
    !updateClicked.current && open && onModalCloseClick && onModalCloseClick();
    setOpen(false);
  };

  return (
    <>
      <PkButton className={buttonClassName} appearance='outlined' width={buttonWidth} onPkClick={handleFilterClick}>
        <ButtonContent state={localState} />
      </PkButton>
      <ReactPortal>
        <PkDialog
          className={clsx(classes.dialog, dialogClassName)}
          pk-theme={dialogTheme}
          isOpen={open}
          onPkOpen={handleOpen}
          onPkClose={handleClose}
          size={isMobile || (!isDesktop && isLandscape) ? 'full' : 'large'}
        >
          <div
            slot='header'
            className={clsx(
              classes.header,
              'pk-w--100 pk-bg-elevation-01 pk-p--m pk-px-sm--xl pk-py-sm--l pk-font--base-semibold pk-text--text-01 pk-line-height--md pk-font-size--xl2',
            )}
            pk-theme={headerTheme}
          >
            {headerText}
          </div>
          <div slot='body' className='pk-d--flex pk-flex--column pk-w--100 pk-px--m pk-px-sm--xl'>
            <Content initialState={initialState} localState={localState} onContentUpdate={handleUpdate} />

            {subContentNote}
          </div>
          <div
            slot='footer'
            className='pk-w--100 pk-d--flex pk-flex--column pk-justify-content--center pk-align-items--center pk-gap--m'
          >
            {hasError && !!errorText && (
              <div className='pk-font--base-regular pk-text--support-04 pk-line-height--md pk-font-size--s'>
                {errorText}
              </div>
            )}
            <div className='pk-w--100 pk-d--flex pk-justify-content--center pk-align-items--center pk-gap--m'>
              <PkButton
                className={classes.footerBtn}
                appearance='contained'
                disabled={!errorText && !validState ? true : undefined}
                onPkClick={handleConfirmClick}
              >
                {t(applyBtnTag)}
              </PkButton>
              <PkButton
                className={classes.footerBtn}
                appearance='outlined'
                disabled={changed ? undefined : true}
                onPkClick={handleClearFilterClick}
              >
                {t(resetBtnTag)}
              </PkButton>
            </div>
          </div>
        </PkDialog>
      </ReactPortal>
    </>
  );
};

export default withErrorBoundary(memo(FilterModal)) as typeof FilterModal;
